import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import "src/components/reusable/CustomChartTooltip/chart-tooltip.scss";
import RestaurantSectionCard from "../RestaurantSectionCard";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";

export interface DonutDatum {
  id: string;
  label: string;
  orders: number;
  revenue: number;
  share: number;
}

interface RestaurantDonutBreakdownProps {
  title: string;
  tip?: string;
  data: DonutDatum[];
  metric?: "orders" | "revenue";
}

const RestaurantDonutBreakdown: FC<RestaurantDonutBreakdownProps> = ({
  title,
  tip,
  data,
  metric = "orders",
}) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();

  return (
    <RestaurantSectionCard title={title} tip={tip}>
      <div className="restaurant-chart restaurant-chart--donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) {return null;}
                const item = payload[0].payload as DonutDatum;
                return (
                  <div className="chart-tooltip-container">
                    <div className="chart-tooltip-label">{item.label}</div>
                    <div className="chart-tooltip-value">
                      <span className="chart-tooltip-title">
                        {t("restaurantAnalytics.charts.orders", { defaultValue: "Orders" })}:
                      </span>
                      <span className="chart-tooltip-number">{fmt.formatNumber(item.orders)}</span>
                    </div>
                    <div className="chart-tooltip-value">
                      <span className="chart-tooltip-title">
                        {t("restaurantAnalytics.charts.revenue", { defaultValue: "Revenue" })}:
                      </span>
                      <span className="chart-tooltip-number">
                        {fmt.formatCurrency(item.revenue, { compact: true })}
                      </span>
                    </div>
                    <div className="chart-tooltip-value">
                      <span className="chart-tooltip-title">
                        {t("restaurantAnalytics.charts.share", { defaultValue: "Share" })}:
                      </span>
                      <span className="chart-tooltip-number">{fmt.formatPercent(item.share)}</span>
                    </div>
                  </div>
                );
              }}
              wrapperStyle={{ pointerEvents: "none" }}
            />
            <Pie
              data={data}
              dataKey={metric}
              nameKey="label"
              innerRadius="55%"
              outerRadius="82%"
              paddingAngle={data.length > 1 ? 2 : 0}
              stroke="var(--color-card, #fff)"
              strokeWidth={2}
            >
              {data.map((item, index) => (
                <Cell key={item.id} fill={CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]} />
              ))}
            </Pie>
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              iconSize={8}
              formatter={value => <span className="restaurant-chart__legend-label">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </RestaurantSectionCard>
  );
};

export default RestaurantDonutBreakdown;
